import { Button, Card, CssBaseline, Grid, makeStyles, Typography } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import defaultTheme from 'src/setup/theme';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    padding: theme.spacing(8),
    textAlign: 'center',
    // without a cap the longest translation of supportText/resetHint stretches the card
    // across the whole viewport
    maxWidth: 420,
  },
  headline: {
    marginBlockEnd: theme.spacing(4),
  },
  supportText: {
    marginBlockEnd: theme.spacing(2),
  },
  button: {
    width: theme.spacing(18),
    display: 'grid',
  },
  resetHint: {
    // variant="caption" renders a <span>, which would swallow the block margin
    display: 'block',
    marginBlockStart: theme.spacing(2),
  },
}));

export const handleAppError = (error: unknown): void => {
  // TODO: log error in external service
  // eslint-disable-next-line no-console
  console.log(error);
};

// Must stay a child of the ThemeProvider below: useStyles() reads the theme from context, so
// calling it in AppErrorFallback itself would resolve against MUI's default theme.
const ErrorCard = (): JSX.Element => {
  const { t } = useTranslation();
  const classes = useStyles();

  const handleClickReload = () => {
    window.location.reload();
  };

  const handleClickReset = () => {
    localStorage.clear();
    handleClickReload();
  };

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <Typography variant="h1" className={classes.headline}>
          {t('appErrorFallback.headline')}
        </Typography>
        <Typography className={classes.supportText}>{t('appErrorFallback.supportText')}</Typography>
        <Grid container spacing={1}>
          <Grid item xs={6} className={classes.button}>
            <Button color="secondary" variant="outlined" onClick={handleClickReset}>
              {t('appErrorFallback.reset')}
            </Button>
          </Grid>
          <Grid item xs={6} className={classes.button}>
            <Button color="primary" variant="contained" onClick={handleClickReload}>
              {t('appErrorFallback.reload')}
            </Button>
          </Grid>
        </Grid>
        <Typography variant="caption" color="textSecondary" className={classes.resetHint}>
          {t('appErrorFallback.resetHint')}
        </Typography>
      </Card>
    </div>
  );
};

// This fallback replaces <App />, where the app's ThemeProvider lives, so it has to bring its own.
// The crash screen deliberately always shows GW Scout branding, never the customer-mode one.
export const AppErrorFallback = (): JSX.Element => (
  <ThemeProvider theme={defaultTheme}>
    <CssBaseline />
    <ErrorCard />
  </ThemeProvider>
);
